import { useUploadMedia } from "@/shared";
import { track } from "@/features/analytics";
import { useAppDispatch, useAppSelector } from "@store";
import { editEntity } from "@store/story";
import { getEntitySelectedSetting } from "@store/story/selectors";
import { setBoundOrFieldValue } from "@store/story/thunks/set-bound-or-field-value";
import { toast } from "react-toastify";
import type { CropData } from "../types";
import { openImageCropper } from "../utils/open-image-cropper";

/**
 * Shared hook that wires up crop dialog selectors and dispatch logic.
 * Used by crop-entity-action, DialogControl, and the Image settings control.
 */
export const useCropDialog = (
  entityId: string,
  keys?: { src: string; cropOriginalSrc: string; cropData: string },
) => {
  const dispatch = useAppDispatch();
  const { uploadMedia } = useUploadMedia();

  const srcKey = keys?.src ?? "src";
  const cropOriginalSrcKey = keys?.cropOriginalSrc ?? "cropOriginalSrc";
  const cropDataKey = keys?.cropData ?? "cropData";

  const src = useAppSelector((state) =>
    getEntitySelectedSetting<string>(srcKey, entityId)(state),
  );
  const cropOriginalSrc = useAppSelector((state) =>
    getEntitySelectedSetting<string>(cropOriginalSrcKey, entityId)(state),
  );
  const cropData = useAppSelector((state) =>
    getEntitySelectedSetting<CropData | undefined>(cropDataKey, entityId)(state),
  ) as CropData | undefined;
  const editorShape = useAppSelector((state) =>
    getEntitySelectedSetting<string | undefined>("shape", entityId)(state),
  );

  const triggerCropDialog = () => {
    const imageUrl = cropOriginalSrc || src || "";
    openImageCropper({
      imageSrc: src ?? "",
      originalImageSrc: cropOriginalSrc || undefined,
      defaultCropData: cropData ?? undefined,
      editorShape: editorShape ?? undefined,
      onCropAndUpload: (mediaFile) => {
        dispatch(
          setBoundOrFieldValue({
            entityId,
            key: srcKey,
            bindingKey: "dynamicVariableSrc",
            value: mediaFile.url,
            // Include cropOriginalSrc so the reducer knows this is a crop,
            // not an image swap (which should clear crop data).
            extra: { cropOriginalSrc: imageUrl },
          }),
        );
      },
      onBackgroundUpload: async (blob, blobUrl) => {
        try {
          const mediaFile = await uploadMedia({
            blob,
            fileType: blob.type,
          });
          dispatch(
            setBoundOrFieldValue({
              entityId,
              key: srcKey,
              bindingKey: "dynamicVariableSrc",
              value: mediaFile.url,
              extra: { cropOriginalSrc: imageUrl },
            }),
          );
        } catch (e) {
          console.error(e);
          track("failed to crop image", { error: e });
          toast.error("Failed to upload cropped image");
        } finally {
          URL.revokeObjectURL(blobUrl);
        }
      },
      onCropDataSave: (data) => {
        dispatch(
          editEntity({
            id: entityId,
            data: {
              [cropOriginalSrcKey]: imageUrl,
              [cropDataKey]: data,
              ...(data.shape != null && { shape: data.shape }),
            },
          }),
        );
      },
    });
  };

  return { src, cropOriginalSrc, cropData, triggerCropDialog };
};
